import type { AiFocus } from '@roll1/shared';

/**
 * The seam between «Замечания» (the panel, the game hint at an objective) and Tia's chat (group E).
 *
 * The panel never owns a chat: a button of Remarks is a REQUEST to Tia, and the chat that is on screen (the Studio
 * column, or the table chat when the table is shown) takes it — opens itself if it is closed, writes the line into its
 * conversation, sends it with `mode: 'auto'` whatever its «Allow changes» box says (pressing the button IS the request).
 * Plain module-level subscribers, no window events: the first subscriber that returns `true` has taken the request.
 *
 * And the other way: the model pass tells the stage how many remarks it found («нашла N замечаний»), and the stage's
 * line «Замечаний: N. Показать?» asks Studio to open the panel.
 */

export interface AiAskRequest {
  /** The text sent as the GM's message. */
  message: string;
  /** Where the request is about (a remark's thing); absent — the chat's own focus. */
  focus?: AiFocus;
  /** «Обсудить»: the remark that becomes the topic of the conversation. `title` is shown in the chat as «Тема: …». */
  topic?: { remarkKey: string; title: string };
}

/** A fix the SERVER does as one undoable answer (`remark:fix` / `remark:apply-hint`); the chat names the conversation and shows the card. */
export interface AiRemarkAction {
  key: string;
  /** The remark's title as shown in the panel (the chat writes «Исправь: <title>» / «Отметить: <title>»). */
  title: string;
}

type Handler<T> = (req: T) => boolean | void;

function channel<T>(): { subscribe: (h: Handler<T>) => () => void; publish: (req: T) => boolean } {
  const handlers = new Set<Handler<T>>();
  return {
    subscribe(h) { handlers.add(h); return () => { handlers.delete(h); }; },
    publish(req) {
      for (const h of [...handlers]) if (h(req) === true) return true;
      return false;
    },
  };
}

const ask = channel<AiAskRequest>();
const fix = channel<AiRemarkAction>();
const hint = channel<AiRemarkAction>();
const found = channel<number>();
const open = channel<void>();

/** Remarks → chat: ask Tia (mode 'auto', the given focus / topic). Returns false when no chat is mounted. */
export const requestAiAsk = ask.publish;
export const onAiAsk = ask.subscribe;
/** Remarks → chat: `remark:fix` for this key, its card in the Studio column's conversation. */
export const requestRemarkFix = fix.publish;
export const onRemarkFix = fix.subscribe;
/** Game hint → chat: `remark:apply-hint` for this key, its card in the table chat's conversation. */
export const requestApplyHint = hint.publish;
export const onApplyHint = hint.subscribe;
/** Model pass (the panel's «Check the prep») → stage: it found `count` remarks. */
export const announceRemarksFound = found.publish;
export const onRemarksFound = found.subscribe;
/** Stage («Замечаний: N. Показать?») → Studio: open the «Замечания» panel. */
export const requestOpenRemarks = (): boolean => open.publish(undefined);
export const onOpenRemarks = open.subscribe;
